import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';

const Login = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const [devMode, setDevMode] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    fetch('/api/auth/dev-status').then(r => r.json()).then(d => setDevMode(d.devMode)).catch(() => {});
  }, []);

  const handleDevLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch('/api/auth/dev-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        window.location.href = '/dashboard';
      } else {
        const data = await res.json();
        setLoginError(data.error || 'Login failed');
      }
    } catch {
      setLoginError('Connection error');
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const errorMessages = {
    auth_failed: 'Google authentication failed. Please try again.',
    no_access: 'Access restricted. Only Admin, Senior Management, and Project Managers can log in.',
    server_error: 'Something went wrong. Please try again.',
  };

  const styles = {
    container: {
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '100vh', backgroundColor: '#1a1a2e',
      backgroundImage: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    },
    card: {
      backgroundColor: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)',
      borderRadius: '16px', padding: '48px', width: '100%', maxWidth: '420px',
      border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      textAlign: 'center',
    },
    logo: { fontSize: '48px', marginBottom: '12px' },
    title: { fontSize: '28px', fontWeight: '800', color: '#fff', marginBottom: '8px' },
    subtitle: { fontSize: '14px', color: '#bbb', marginBottom: '32px' },
    googleBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
      width: '100%', padding: '14px 24px', backgroundColor: '#fff',
      color: '#333', border: 'none', borderRadius: '8px', fontSize: '15px',
      fontWeight: '600', cursor: 'pointer', textDecoration: 'none',
      transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    error: {
      padding: '12px 16px', backgroundColor: 'rgba(244,67,54,0.15)',
      border: '1px solid rgba(244,67,54,0.3)', borderRadius: '8px',
      color: '#ef9a9a', fontSize: '13px', marginBottom: '24px',
    },
    note: { fontSize: '12px', color: '#666', marginTop: '24px', lineHeight: '1.6' },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>📊</div>
        <div style={styles.title}>PAS</div>
        <div style={styles.subtitle}>Performance Appraisal System — We Are Amnet</div>

        {error && <div style={styles.error}>{errorMessages[error] || 'Login failed.'}</div>}

        <a
          href="/auth/google"
          style={styles.googleBtn}
          onMouseEnter={e => { e.target.style.backgroundColor = '#f5f5f5'; e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)'; }}
          onMouseLeave={e => { e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'; }}
        >
          <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.76-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.87 7.34 2.44 10.52l8.09-5.93z"/><path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Sign in with Google
        </a>

        {devMode && (
          <div style={{ marginTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '24px' }}>
            <div style={{ fontSize: '13px', color: '#aaa', marginBottom: '12px' }}>Dev Mode — Username/Password Login</div>
            <form onSubmit={handleDevLogin}>
              <input type="text" placeholder="Username (e.g. Admin)" value={username} onChange={e => setUsername(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: '8px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }} />
              <input type="password" placeholder="Password (PAS@1234)" value={password} onChange={e => setPassword(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: '12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }} />
              {loginError && <div style={{ color: '#ef9a9a', fontSize: '12px', marginBottom: '8px' }}>{loginError}</div>}
              <button type="submit" style={{ width: '100%', padding: '12px', backgroundColor: '#e94560', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                Sign In (Dev)
              </button>
            </form>
          </div>
        )}

        <div style={styles.note}>
          Restricted to @weareamnet.com accounts.<br />
          Admin, Senior Management, and Project Managers only.
        </div>
      </div>
    </div>
  );
};

export default Login;
